/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { ApiKeyV2WithKey } from "../../../data/interfaces";
import React from "react";
import GreyButton from "../../../elements/GreyButton";
import useStyles from "./style";
import CopyTextArea from "../../../elements/CopyTextArea";
import { Info, Warning } from "@material-ui/icons";


interface ApiKeyViewProps {
    generatedApiKey: ApiKeyV2WithKey;
    isInProgress: boolean;
    isSubscriptionApprovalRequired?: boolean;
    onClose: () => void;
}

function ApiKeyView(props: ApiKeyViewProps) {
    const { generatedApiKey, isInProgress, isSubscriptionApprovalRequired, onClose } = props;
    const classes = useStyles();
    return (
         <>
            <Box
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection="column"
            height="100%"
        > 
            {isSubscriptionApprovalRequired && (
                <Box className={classes.keyInfoWarning}>
                    <Box display="flex" flexDirection="row" justifyContent="space-between" mb={1}>
                        <Box>
                            <Typography variant='h5' className={classes.infoHeader}>
                                <FormattedMessage
                                    id='components.shared.ApiKeyView.copyApiKey.label'
                                    defaultMessage='Copy the API Key'
                                />
                            </Typography>
                        </Box>
                        <Box display="flex">
                            <Warning className={classes.warningIcon} />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant='body1' className={classes.infoText}>
                            <FormattedMessage
                                id='components.shared.ApiKeyView.copyApiKey.subscriptionWarning'
                                defaultMessage='Please copy this generated API Key as
                                it will not be visible in the UI again. The Key will work once the subscription is approved.'
                            />
                        </Typography>
                    </Box>
                </Box>
            )}
            { !isSubscriptionApprovalRequired && (
                <Box className={classes.keyInfo}>
                    <Box display="flex" flexDirection="row" justifyContent="space-between" mb={1}>
                        <Box>
                            <Typography variant='h5' className={classes.infoHeader}>
                                <FormattedMessage
                                    id='components.shared.ApiKeyView.copyApiKey.label'
                                    defaultMessage='Copy the API Key'
                                />
                            </Typography>
                        </Box>
                        <Box display="flex">
                            <Info className={classes.infoIcon} />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant='body1' className={classes.infoText}>
                            <FormattedMessage
                                id='components.shared.ApiKeyView.copyApiKey.instruction'
                                defaultMessage='Please copy this generated API Key as
                                it will not be visible in the UI again.'
                            />
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box
                alignItems="center"
                flexDirection="row"
                my={1}
                width={1}
            >
                <CopyTextArea rows={2} textContent={generatedApiKey?.value} />
            </Box>
            <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                <GreyButton
                    disabled={isInProgress}
                    onClick={onClose}
                    className={classes.cancelBtn}
                    buttonLabel={"Close"}
                />
            </Box>
        </Box>
        </>
        );
}

export default ApiKeyView;
